import React from "react";
import { Printer } from "lucide-react";

export default function GettingStartedPage() {
  const printPDF = () => {
    window.print();
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Guide</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tighter">Getting Started.</h1>
        </div>
        <button
          onClick={printPDF}
          className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] transition-colors"
        >
          <Printer className="w-4 h-4" />
          Print / Save as PDF
        </button>
      </div>

      <div className="bg-white dark:bg-[#121214] border border-slate-200 dark:border-slate-800 rounded-sm p-8 sm:p-12 text-slate-900 dark:text-slate-100 print:border-none print:shadow-none">
        <div className="prose prose-sm sm:prose dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-6">Getting Started with BillForge</h1>
          
          <p className="text-lg mb-6">Welcome to BillForge - a simple and powerful invoicing solution that helps you create professional invoices in minutes. This guide will walk you through creating your first invoice.</p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 1: Create Your Account</h2>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>Visit the BillForge website</li>
            <li>Click "Sign Up"</li>
            <li>Enter your email, name, and password</li>
            <li>Click "Create Account"</li>
          </ol>
          <p className="mb-6">You'll be taken to the onboarding screen to set up your organization.</p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 2: Set Up Your Organization</h2>
          <p className="mb-4">Before creating invoices, you need to set up your organization details:</p>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li><strong>Organization Name</strong>: Enter your business name</li>
            <li><strong>Business Owner</strong>: Your name or the business owner's name</li>
            <li><strong>Contact Details</strong>: Email and phone number</li>
            <li><strong>Address</strong>: Your business address</li>
            <li><strong>GST Number</strong>: If applicable, enter your GST number</li>
            <li><strong>Bank Details</strong>:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li>Bank name</li>
                <li>Account number</li>
                <li>IFSC code</li>
                <li>UPI ID (optional)</li>
              </ul>
            </li>
          </ol>
          <p className="mb-6">These details will appear on your invoices, so ensure they are accurate. Click "Save Organization" when complete.</p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 3: Create Your First Customer</h2>
          <p className="mb-4">To send an invoice, you need a customer:</p>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>Navigate to <strong>Customers</strong> from the sidebar</li>
            <li>Click <strong>"+ Add Customer"</strong></li>
            <li>Fill in the customer details:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Customer Name</strong>: The business or client name</li>
                <li><strong>Contact Person</strong>: The person you're dealing with</li>
                <li><strong>Phone</strong>: Contact number</li>
                <li><strong>Email</strong>: Email address for sending invoices</li>
                <li><strong>GST</strong>: Customer's GST number (if applicable)</li>
                <li><strong>Address</strong>: Customer's billing address</li>
                <li><strong>City, State, Country, Pincode</strong>: Location details</li>
              </ul>
            </li>
            <li>Click <strong>"Save Customer"</strong></li>
          </ol>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 4: Create an Invoice Template (Optional)</h2>
          <p className="mb-4">BillForge comes with default templates, but you can customize them:</p>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>Navigate to <strong>Templates</strong> from the sidebar</li>
            <li>Click <strong>"+ Create Template"</strong></li>
            <li>Customize your template:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Layout Type</strong>: Choose from Tally or GST</li>
                <li><strong>Colors</strong>: Primary and secondary colors</li>
                <li><strong>Font</strong>: Choose your preferred font family</li>
                <li><strong>Invoice Title</strong>: Customize the invoice title (e.g., "TAX INVOICE", "INVOICE")</li>
                <li><strong>Item Columns</strong>: Choose which columns to display in the invoice items table</li>
                <li><strong>Show Powered By</strong>: Toggle the "Powered by BillForge" footer text</li>
              </ul>
            </li>
            <li>Click <strong>"Save Template"</strong></li>
          </ol>
          <p className="mb-6">You can set a template as default for all new invoices.</p>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 5: Create Your First Invoice</h2>
          <p className="mb-4">Now you're ready to create your first invoice:</p>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>Navigate to <strong>Invoices</strong> from the sidebar</li>
            <li>Click <strong>"+ New Invoice"</strong> or the <strong>"New Invoice"</strong> button in the sidebar</li>
          </ol>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.1 Select Customer</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Choose an existing customer from the dropdown, or</li>
            <li>Click <strong>"New"</strong> to create a customer on the fly</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.2 Select Template</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Choose a template for this invoice</li>
            <li>The default template will be selected automatically</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.3 Fill Invoice Details</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li><strong>Invoice Number</strong>: Auto-generated based on your organization's prefix</li>
            <li><strong>Invoice Date</strong>: Select the invoice date</li>
            <li><strong>Due Date</strong>: Select when payment is due (based on your credit period setting)</li>
            <li><strong>Reverse Charge</strong>: Check if applicable</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.4 Add Shipping Details (Optional)</h3>
          <p className="mb-4">If you need to ship to a different address:</p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Click <strong>"Ship To Customer"</strong></li>
            <li>Choose an existing customer or create a new one</li>
            <li>Or click <strong>"Same as Bill To"</strong> to use the billing address</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.5 Add Transport Details (Optional)</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li><strong>Transport Mode</strong>: Enter transport details</li>
            <li><strong>E-Way Bill Number</strong>: If applicable</li>
            <li><strong>IRN Number</strong>: If applicable</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.6 Add Invoice Items</h3>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>Click <strong>"+ Add Item"</strong></li>
            <li>Fill in item details:
              <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                <li><strong>Item Name</strong>: Product or service name</li>
                <li><strong>Description</strong>: Detailed description (optional)</li>
                <li><strong>HSN/SAC</strong>: HSN or SAC code</li>
                <li><strong>Quantity</strong>: Number of units</li>
                <li><strong>Unit</strong>: Unit of measurement (e.g., pcs, kg, hrs)</li>
                <li><strong>Price</strong>: Price per unit</li>
                <li><strong>Amount</strong>: Automatically calculated (Quantity × Price)</li>
              </ul>
            </li>
            <li>Add as many items as needed</li>
          </ol>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.7 Set Tax Details</h3>
          <p className="mb-4">The invoice will automatically calculate:</p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li><strong>Subtotal</strong>: Sum of all item amounts</li>
            <li><strong>Discount</strong>: Enter discount amount if applicable</li>
            <li><strong>Shipping</strong>: Enter shipping charges if applicable</li>
            <li><strong>CGST</strong>: Central GST (calculated based on item HSN codes)</li>
            <li><strong>SGST</strong>: State GST (calculated based on item HSN codes)</li>
            <li><strong>IGST</strong>: Integrated GST (for interstate transactions)</li>
            <li><strong>Roundoff</strong>: Automatic rounding adjustment</li>
            <li><strong>Total</strong>: Final invoice amount</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.8 Add Notes and Terms (Optional)</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li><strong>Notes</strong>: Add any additional notes for the customer</li>
            <li><strong>Terms</strong>: Add payment terms and conditions</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">5.9 Preview and Save</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Click <strong>"Preview"</strong> to see how your invoice will look</li>
            <li>Click <strong>"Save"</strong> to save the invoice</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 6: Download or Share Your Invoice</h2>
          <p className="mb-4">After saving your invoice:</p>
          <ol className="list-decimal list-inside space-y-2 mb-6">
            <li>Navigate to <strong>Invoices</strong> from the sidebar</li>
            <li>Click on your invoice to view details</li>
            <li>Click <strong>"Download PDF"</strong> to download the invoice as a PDF</li>
            <li>Share the PDF with your customer via email or any other method</li>
          </ol>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Step 7: Track Invoice Status</h2>
          <p className="mb-4">From the Invoices page, you can:</p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>View all your invoices</li>
            <li>Filter by status (Pending, Paid, Overdue, Cancelled)</li>
            <li>Click on an invoice to view details</li>
            <li>Update invoice status</li>
            <li>Add payments to invoices</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Tips for Using BillForge</h2>

          <h3 className="text-xl font-semibold mt-6 mb-3">Organization Settings</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Set up your <strong>Invoice Prefix</strong> in organization settings (e.g., "INV-", "BILL-")</li>
            <li>Configure <strong>Credit Period</strong> to auto-calculate due dates</li>
            <li>Add default <strong>Notes</strong> and <strong>Terms</strong> to save time on each invoice</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Customer Management</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Keep customer details updated for accurate invoicing</li>
            <li>Use the search feature to quickly find customers</li>
            <li>Edit customer details anytime from the Customers page</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Invoice Templates</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Create multiple templates for different clients or purposes</li>
            <li>Use the <strong>"Set as Default"</strong> option to use a template for all new invoices</li>
            <li>Customize item columns to show only relevant information</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Invoice Items</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Use HSN/SAC codes for accurate tax calculation</li>
            <li>Add detailed descriptions for clarity</li>
          </ul>

          <h3 className="text-xl font-semibold mt-6 mb-3">Dashboard</h3>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li>Monitor your business overview from the Dashboard</li>
            <li>View recent invoices, total revenue, and outstanding payments</li>
            <li>Track payment status at a glance</li>
          </ul>

          <h2 className="text-2xl font-semibold mt-8 mb-4">Settings</h2>
          <p className="mb-4">Access settings by clicking the gear icon in the sidebar footer:</p>
          <ul className="list-disc list-inside space-y-2 mb-6">
            <li><strong>Theme</strong>: Switch between light and dark mode</li>
            <li><strong>Logout</strong>: Sign out of your account</li>
            <li><strong>Delete Account</strong>: Permanently delete your account and all data (requires password confirmation)</li>
          </ul>

          <p className="text-lg font-semibold mt-8">Happy Invoicing with BillForge! 🚀</p>
        </div>
      </div>
    </div>
  );
}
